import useSWR, { SWRConfiguration, SWRResponse } from 'swr';

import { useApplicationConfig } from '~/data/applicationConfig';
import {
  ProposalAgent,
  ProposalPreviewProps,
  ProposalType,
} from '~/data/proposals/types';
import { fetchFromOldApi } from '~/services/Http';

export type ProposalPreviewRawData = {
  fProposalTracking: ProposalPreviewProps;
  fInitiatorPermissions: ProposalAgent[];
};

export const getProposalPreviewData = (
  proposalType: string,
  targetId: number | string,
): Promise<ProposalPreviewRawData> => {
  return fetchFromOldApi(
    `/json/workflows/preview/${proposalType}/${targetId}`,
  ).then((r) => r.json());
};

const processProposalPreviewData = (
  proposalPreview?: ProposalPreviewRawData,
): ProposalPreviewProps | undefined => {
  return !!proposalPreview?.fProposalTracking
    ? {
        ...proposalPreview.fProposalTracking,
        permissions: {
          initiators: proposalPreview.fInitiatorPermissions || [],
        },
      }
    : undefined;
};

export type GetProposalPreviewProps = {
  proposalType: ProposalType;
  targetId: number;
};

export type UseGetProposalPreviewOutput = Omit<
  SWRResponse<ProposalPreviewRawData>,
  'data'
> & {
  data: ProposalPreviewProps | undefined;
};

export const useGetProposalPreview = (
  props: GetProposalPreviewProps,
  swrConfig?: SWRConfiguration,
): UseGetProposalPreviewOutput => {
  const { proposalType, targetId } = props;

  const { applicationConfig, hasData } = useApplicationConfig();
  const workflowsEngineEnabled = hasData
    ? applicationConfig.fComConfig.miscellaneousConfig
        .fComEnableNewWorkflowsEngine
    : false;

  const proposalSwrConfig = {
    shouldRetryOnError: false,
    revalidateOnFocus: false,
    ...swrConfig,
  };

  const { data, ...rest } = useSWR(
    workflowsEngineEnabled ? ['proposalPreview', proposalType, targetId] : null,
    () => getProposalPreviewData(proposalType, targetId),
    proposalSwrConfig,
  );
  return {
    data: processProposalPreviewData(data),
    ...rest,
  };
};
